/**
 * Look page (sections/main-look.liquid).
 *
 * Desktop rows (snippets/look-item.liquid):
 * - clicking a size selects it for that row; adding without a size shows "Please select size" instead
 * - the size guide of the row picks a size through "size-guide:select" and follows the row selection
 *   through "variant:changed", the event it listens to on the product page
 * - adding without a size also shows the look message ("Please fill in missing information…")
 * - once added, the button reads "Added to cart" until the added-to-cart popup closes
 *
 * "+ Add full look" adds every product of the look in one request, each with its size:
 * - desktop: the size selected in each row; rows without one show "Please select size", the look shows
 *   "Please fill in missing information…" and nothing is added
 * - below desktop: the size pickers of the strip open one after the other for the products that need a
 *   size (component-complete-the-look.js), then everything is added
 * The added-to-cart popup is told beforehand ("look:adding") so it announces the look — title, photo and
 * number of items — instead of its first product, and leaves out its recommendations.
 */
const LOOK_DESKTOP_MEDIA = '(min-width: 1000px)';

class LookPage extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.items = Array.from(this.querySelectorAll('[data-look-item]'));
    this.strip = this.querySelector('complete-the-look');
    this.addAllButton = this.querySelector('[data-look-add-all]');
    this.addAllError = this.querySelector('[data-look-add-all-error]');

    this.items.forEach((item) => this.setupItem(item, signal));
    this.addAllButton?.addEventListener('click', () => this.addFullLook(), { signal });
    this.strip?.addEventListener('complete-look:select', (event) => this.onStripSelect(event), { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  /* ---------- Desktop rows ---------- */

  setupItem(item, signal) {
    const form = item.querySelector('form');

    item.querySelectorAll('[data-size-guide-for]').forEach((button) => {
      button.addEventListener('click', () => this.selectSize(item, button.dataset.id), { signal });
    });

    // Capture phase on the row, so a row without size never reaches the theme product form submit
    item.addEventListener('submit', (event) => {
      if (!this.variantFor(item)) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleError(item, true);

        if (this.addAllError) {
          this.addAllError.hidden = false;
        }
      }
    }, { capture: true, signal });

    form?.addEventListener('size-guide:select', (event) => this.selectSize(item, event.detail.variantId), { signal });
    form?.addEventListener('variant:added', () => this.showAdded(item), { signal });
  }

  // "Added to cart" for as long as the added-to-cart popup stays open (a few seconds when it is not there)
  showAdded(item) {
    const button = item.querySelector('.look-item__add');
    const label = button?.querySelector('span');

    if (!button || !label) {
      return;
    }

    button.dataset.labelDefault ??= label.textContent;
    label.textContent = button.dataset.labelAdded;

    clearTimeout(button.addedTimeout);
    button.addedObserver?.disconnect();

    const restore = () => {
      button.addedObserver?.disconnect();
      clearTimeout(button.addedTimeout);
      label.textContent = button.dataset.labelDefault;
    };

    const popup = document.getElementById('product-added-popup');

    if (!popup) {
      button.addedTimeout = setTimeout(restore, 3000);
      return;
    }

    // The popup opens on the same event: wait for it to show, then for it to hide again
    button.addedObserver = new MutationObserver(() => {
      if (popup.hidden && button.addedShown) {
        button.addedShown = false;
        restore();
      } else if (!popup.hidden) {
        button.addedShown = true;
      }
    });
    button.addedShown = !popup.hidden;
    button.addedObserver.observe(popup, { attributes: true, attributeFilter: ['hidden'] });

    // Safety net if the popup never opens (adds made with the cart drawer open are ignored by it)
    button.addedTimeout = setTimeout(() => {
      if (!button.addedShown) {
        restore();
      }
    }, 3000);
  }

  selectSize(item, variantId) {
    const form = item.querySelector('form');
    let selected = null;

    item.querySelectorAll('[data-size-guide-for]').forEach((button) => {
      const isSelected = button.dataset.id === String(variantId) && !button.disabled;

      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      if (isSelected) {
        selected = button;
      }
    });

    if (!selected || !form) {
      return;
    }

    form.querySelector('input[name="id"]').value = selected.dataset.id;
    this.toggleError(item, false);

    if (this.addAllError && this.items.every((row) => this.variantFor(row))) {
      this.addAllError.hidden = true;
    }

    // Same event as the product page form, which keeps the size guide column in step
    form.dispatchEvent(new CustomEvent('variant:changed', {
      detail: { variant: { id: Number(selected.dataset.id), options: [selected.querySelector('.value').textContent.trim()] } }
    }));
  }

  variantFor(item) {
    return item.dataset.variantId || item.querySelector('input[name="id"]')?.value || '';
  }

  toggleError(item, visible) {
    const error = item.querySelector('[data-look-item-error]');

    if (error) {
      error.hidden = !visible;
    }

    item.classList.toggle('look-item--error', visible);
  }

  /* ---------- Add full look ---------- */

  addFullLook() {
    if (window.matchMedia(LOOK_DESKTOP_MEDIA).matches) {
      this.addFullLookFromRows();
    } else {
      this.startStripFlow();
    }
  }

  addFullLookFromRows() {
    const missing = this.items.filter((item) => !this.variantFor(item));

    this.items.forEach((item) => this.toggleError(item, missing.includes(item)));

    if (this.addAllError) {
      this.addAllError.hidden = missing.length === 0;
    }

    if (missing.length) {
      missing[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this.addItems(this.items.map((item) => this.variantFor(item)));
  }

  // Single-size cards are known already; the others get their picker opened in turn
  startStripFlow() {
    const cards = Array.from(this.strip?.querySelectorAll('[data-complete-look-card]') || []);

    this.stripFlow = {
      cards,
      chosen: new Map(
        cards
          .map((card) => [card, card.querySelector('.complete-look__add[data-variant-id]')?.dataset.variantId])
          .filter(([, variantId]) => variantId)
      )
    };

    this.continueStripFlow();
  }

  continueStripFlow() {
    const { cards, chosen } = this.stripFlow;
    const next = cards.find((card) => !chosen.has(card));

    if (!next) {
      this.stripFlow = null;
      this.addItems(cards.map((card) => chosen.get(card)));
      return;
    }

    next.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });

    // After the current click has finished bubbling, or the strip would read it as a click outside
    setTimeout(() => this.strip.openPicker(next));
  }

  onStripSelect(event) {
    if (!this.stripFlow) {
      return;
    }

    event.preventDefault();
    this.stripFlow.chosen.set(event.detail.card, event.detail.variantId);
    this.continueStripFlow();
  }

  async addItems(variantIds) {
    if (!variantIds.length || typeof addToCart !== 'function' || this.addAllButton?.hasAttribute('aria-busy')) {
      return;
    }

    this.addAllButton?.setAttribute('aria-busy', 'true');

    const { lookTitle, lookItems, lookImage, lookImageRetina } = this.addAllButton?.dataset || {};

    document.documentElement.dispatchEvent(new CustomEvent('look:adding', {
      bubbles: true,
      detail: { title: lookTitle, items: lookItems, image: lookImage, image2x: lookImageRetina }
    }));

    try {
      await addToCart({ items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })) }, true, false, false);
    } finally {
      this.addAllButton?.removeAttribute('aria-busy');
    }
  }
}

if (!window.customElements.get('look-page')) {
  window.customElements.define('look-page', LookPage);
}
